/**
 * MK-07: the feature pages of the marketing site (`/features`, `/features/<page>`), part of the `web` namespace.
 * Every claim is something the product does today (docs/PROGRESS.md).
 */
export const featurePages = {
    index: {
        metaTitle: "Features",
        eyebrow: "Features",
        title: "Everything a Bangladeshi shop runs on, in one system",
        body: "The counter, Facebook and website orders, couriers, stock across branches, and the accounts behind them — all sharing one stock and one set of numbers.",
        more: "Learn more",
    },
    common: {
        seeLive: "See it live",
        seeLiveBody: "Open a real demo shop, or get your own demo business with the Admin and the POS — no sign-up.",
        visitShop: "Visit the live demo shop",
        tryIt: "Try it yourself",
        related: "Also in {{brand}}",
        forIndustries: "Made for",
    },
    pos: {
        nav: "POS",
        metaDescription: "An offline-first point of sale for Windows and Mac: barcode scanning, Bengali receipts, held bills, returns, shifts and cash counts — synced with your online stock.",
        eyebrow: "Point of sale",
        title: "A counter that never stops billing",
        body: "Install the POS on the counter computer. It sells from its own copy of your catalogue, so a slow or dead connection never keeps a customer waiting.",
        summary: "Fast offline billing with Bengali receipts, returns and shift cash counts.",
        blocks: {
            a: {
                title: "Fast at the counter",
                body: "Built for the keyboard and the scanner, with big touch targets for the busy hour.",
                points: {
                    p1: "Scan a barcode, or search by name in English or Bengali",
                    p2: "Hold a bill and serve the next customer, then pick it up again",
                    p3: "Discounts above a limit need a manager's PIN",
                },
                alt: "The {{brand}} dashboard with the day's sales from the counter and online",
            },
            b: {
                title: "Offline for up to 7 days",
                body: "When the internet drops, billing, printing and payments carry on. Every bill syncs on its own when it is back — in order, never twice.",
                points: {
                    p1: "Receipts print in Bengali on any thermal printer",
                    p2: "Returns and exchanges from the receipt barcode, even from another branch",
                    p3: "The local data is encrypted and backed up every hour",
                },
                alt: "Products and prices that the POS keeps a copy of",
            },
            c: {
                title: "Cash you can trust at closing",
                body: "Each shift opens with a float and closes with a blind cash count, so a shortfall shows the same evening.",
                points: {
                    p1: "X and Z reports, cash in and cash out",
                    p2: "Expected, counted and the difference, per shift",
                    p3: "Every counter's sales in the same reports as your online orders",
                },
                alt: "A sales report that includes every counter",
            },
        },
    },
    "online-orders": {
        nav: "Online orders",
        metaDescription: "Take orders from Facebook, WhatsApp, the phone and your website in one list; check for fake orders, book Steadfast, Pathao or RedX in one click and match COD.",
        eyebrow: "Online orders & couriers",
        title: "From the Facebook message to the courier in one place",
        body: "Orders from your website, Facebook, WhatsApp and the phone land in one list and take stock from the same place as the shop counter.",
        summary: "Every order channel in one list, fake-order check, one-click courier booking and COD matching.",
        blocks: {
            a: {
                title: "Every order in one list",
                body: "Type in a Facebook or phone order in seconds; website orders arrive on their own.",
                points: {
                    p1: "Confirm, pack and ship with clear steps",
                    p2: "Stock is held as soon as an order is placed",
                    p3: "The customer's earlier orders and returns are one tap away",
                },
                alt: "The orders list with website and Facebook orders together",
            },
            b: {
                title: "Couriers and COD without the spreadsheets",
                body: "Book Steadfast, Pathao or RedX from the order, follow the parcel, and match each courier payout to its orders.",
                points: {
                    p1: "One-click booking with the address already filled",
                    p2: "Delivery status updates on its own",
                    p3: "COD collected, charges and the money still owed, per courier",
                },
                alt: "Cash on delivery matched to courier payouts",
            },
            c: {
                title: "Fewer fake orders",
                body: "Before you ship, see how often this phone number has ordered, received and returned — and block the ones that never pay.",
                points: {
                    p1: "A risk score from the order history of the number",
                    p2: "A blocklist for numbers that keep refusing parcels",
                    p3: "A one-time code at checkout when you want it",
                },
                alt: "A product page on a shop's own website",
            },
        },
    },
    inventory: {
        nav: "Stock & branches",
        metaDescription: "One stock for every branch, warehouse, counter and your website: transfers, stock counts, purchase orders and a full history of every change.",
        eyebrow: "Stock & branches",
        title: "One stock, right everywhere",
        body: "Every sale, return, purchase and transfer changes the same stock, so the last piece is never sold twice and every branch knows what it holds.",
        summary: "Live stock per branch, transfers, counts and purchases with a full history.",
        blocks: {
            a: {
                title: "Every branch on one screen",
                body: "See what each branch, warehouse and counter holds right now, and what is held for orders.",
                points: {
                    p1: "Sizes, colours and other variants, each with its own stock",
                    p2: "Low-stock alerts before you run out",
                    p3: "A history of every change: who, when and why",
                },
                alt: "Stock of every product across branches",
            },
            b: {
                title: "Move and count without closing",
                body: "Send stock to another branch and receive it there; count shelves while the shop keeps selling.",
                points: {
                    p1: "Transfers with send and receive steps",
                    p2: "Stock counts that account for sales during the count",
                    p3: "Adjustments with a reason, approved before they post",
                },
                alt: "The product list with prices and stock",
            },
            c: {
                title: "Buy from suppliers with the numbers in front of you",
                body: "Raise purchase orders, receive goods against them and know what you owe each supplier.",
                points: {
                    p1: "Purchase orders and goods receipts",
                    p2: "Supplier dues and payments",
                    p3: "Barcode labels for what you just received",
                },
                alt: "A product on the shop's website, with stock from the same count",
            },
        },
    },
    storefront: {
        nav: "Your website",
        metaDescription: "Your own online shop in one of 12 designs made for Bangladesh: your domain, cash on delivery, bKash and Nagad, landing pages and Facebook Pixel — on the same stock as your shop.",
        eyebrow: "Your own website",
        title: "A shop website that sells from the same stock",
        body: "Pick a design, add your logo and colours, and publish. Products, prices and stock come from the system — nothing to copy across.",
        summary: "12 designs, your domain, COD, bKash and Nagad, on the same stock.",
        blocks: {
            a: {
                title: "12 designs made for Bangladeshi shops",
                body: "Fashion, grocery, gadgets, books, beauty and more — each fast on a phone and ready in Bengali and English.",
                points: {
                    p1: "Your logo, colours, banners and menu",
                    p2: "Your own domain, or a free address to start",
                    p3: "Change the design at any time without losing anything",
                },
                alt: "Choosing a theme in the {{brand}} Admin",
            },
            b: {
                title: "Checkout the way customers pay",
                body: "Cash on delivery, bKash and Nagad, delivery charges by area, and an order form that works on a cheap phone.",
                points: {
                    p1: "Delivery charges for inside and outside Dhaka",
                    p2: "Offers, coupons and free delivery rules",
                    p3: "Customer accounts with their order history",
                },
                alt: "The home page of a fashion shop built with {{brand}}",
            },
            c: {
                title: "Ready for your Facebook ads",
                body: "Landing pages for a single product, and the Facebook Pixel and Conversions API to measure what your ads sell.",
                points: {
                    p1: "Landing pages with the order form on the page",
                    p2: "Facebook Pixel, Conversions API and Google Analytics",
                    p3: "Search engines find your products",
                },
                alt: "A product page in the gadgets theme",
            },
        },
    },
    accounts: {
        nav: "Accounts & reports",
        metaDescription: "Profit and loss by day, branch and channel, cashbook, expenses, supplier dues, COD matching and VAT invoices — added up on their own from every sale.",
        eyebrow: "Accounts & reports",
        title: "Know what you earned today",
        body: "Every sale, return, expense and courier payout is recorded as it happens, so the numbers are ready when you are.",
        summary: "Profit, cash, expenses, COD and VAT, from every channel, without the evening sums.",
        blocks: {
            a: {
                title: "Profit, not just sales",
                body: "See what you earned after the cost of goods, by day, branch or channel.",
                points: {
                    p1: "Profit and loss for any period",
                    p2: "Best sellers and slow movers",
                    p3: "Every report exports to Excel",
                },
                alt: "A profit and loss report",
            },
            b: {
                title: "Every taka has a place",
                body: "Cash drawers, bank accounts and bKash or Nagad wallets each keep their balance; move money between them in a step.",
                points: {
                    p1: "Cashbook and expenses",
                    p2: "Supplier dues and customer dues",
                    p3: "COD collected by couriers, matched to orders",
                },
                alt: "Courier COD matched to orders",
            },
            c: {
                title: "Ready for VAT and your accountant",
                body: "VAT invoices on sales and a VAT report for the period; hand over clean numbers instead of a bag of receipts.",
                points: {
                    p1: "VAT invoices and a VAT report",
                    p2: "Sales by channel, branch and product",
                    p3: "A full audit trail of who changed what",
                },
                alt: "A sales report by day and channel",
            },
        },
    },
    ebooks: {
        nav: "eBooks",
        metaDescription: "Sell eBooks next to printed books: free samples, bKash payment, a reading library for each customer and watermarked pages — in the Boighor bookshop design.",
        eyebrow: "eBooks & digital products",
        title: "Sell eBooks next to your printed books",
        body: "Add an eBook as another edition of a book. Customers pay online and read it straight away in their library.",
        summary: "eBooks beside printed books, with samples, online payment and a reading library.",
        blocks: {
            a: {
                title: "A bookshop design made for Bangla books",
                body: "Authors, publishers and categories, Bengali titles that read well, and printed and eBook editions side by side.",
                points: {
                    p1: "Printed and eBook editions of one book",
                    p2: "Free sample chapters",
                    p3: "Author and publisher pages",
                },
                alt: "The home page of the Boighor bookshop design",
            },
            b: {
                title: "Paid online, read at once",
                body: "The customer pays with bKash, and the book appears in their library, ready to read on a phone or a computer.",
                points: {
                    p1: "A personal library with the last page read",
                    p2: "Sign-in with phone number or Google",
                    p3: "Printed books still go by courier, in the same order",
                },
                alt: "A book page with its printed and eBook editions",
            },
            c: {
                title: "Protected from easy copying",
                body: "Books open in the reader, not as a file to forward, and every page carries the buyer's own watermark.",
                points: {
                    p1: "Short-lived links, never a public file",
                    p2: "Watermarked samples and pages",
                    p3: "A record of when each book was opened",
                },
                alt: "Books in the {{brand}} product list",
            },
        },
    },
};
//# sourceMappingURL=web-features.js.map